import {
  normalizeFacebookUrl,
  normalizeInstagramUrl,
  normalizeTikTokUrl,
  normalizeXUrl,
  normalizeYouTubeUrl,
  socialHandleFromUrl,
} from "@/lib/social-urls"

export type SocialEmbedPlatform =
  | "instagram"
  | "tiktok"
  | "x"
  | "facebook"
  | "youtube"

export type SocialEmbedKind =
  | "instagram"
  | "tiktok-iframe"
  | "twitter-timeline"
  | "facebook-page"
  | "youtube-channel"
  | "link-fallback"

export interface SocialEmbedSlide {
  id: SocialEmbedPlatform
  platform: SocialEmbedPlatform
  label: string
  url: string
  kind: SocialEmbedKind
  /** TikTok profile iframe src, when kind is tiktok-iframe. */
  iframeSrc?: string
}

export interface BarberSocialFields {
  instagram_url?: string | null
  tiktok_url?: string | null
  x_url?: string | null
  facebook_url?: string | null
  youtube_url?: string | null
}

const PLATFORM_LABELS: Record<SocialEmbedPlatform, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  x: "X",
  facebook: "Facebook",
  youtube: "YouTube",
}

/** Build one swipeable slide per connected social profile. Booking URLs excluded. */
export function buildBarberSocialEmbedSlides(profile: BarberSocialFields): SocialEmbedSlide[] {
  const slides: SocialEmbedSlide[] = []

  const instagram = normalizeInstagramUrl(profile.instagram_url)
  if (instagram) {
    slides.push({
      id: "instagram",
      platform: "instagram",
      label: PLATFORM_LABELS.instagram,
      url: instagram.endsWith("/") ? instagram : `${instagram}/`,
      kind: "instagram",
    })
  }

  const tiktok = normalizeTikTokUrl(profile.tiktok_url)
  const tiktokHandle = socialHandleFromUrl(tiktok)
  if (tiktok && tiktokHandle) {
    slides.push({
      id: "tiktok",
      platform: "tiktok",
      label: PLATFORM_LABELS.tiktok,
      url: tiktok,
      kind: "tiktok-iframe",
      iframeSrc: `https://www.tiktok.com/embed/@${encodeURIComponent(tiktokHandle)}`,
    })
  }

  const x = normalizeXUrl(profile.x_url)
  if (x) {
    slides.push({
      id: "x",
      platform: "x",
      label: PLATFORM_LABELS.x,
      url: x,
      kind: "twitter-timeline",
    })
  }

  const facebook = normalizeFacebookUrl(profile.facebook_url)
  if (facebook) {
    slides.push({
      id: "facebook",
      platform: "facebook",
      label: PLATFORM_LABELS.facebook,
      url: facebook,
      kind: "facebook-page",
    })
  }

  const youtube = normalizeYouTubeUrl(profile.youtube_url)
  if (youtube) {
    slides.push({
      id: "youtube",
      platform: "youtube",
      label: PLATFORM_LABELS.youtube,
      url: youtube,
      kind: "youtube-channel",
    })
  }

  return slides
}

export function facebookPagePluginSrc(pageUrl: string, width = 500): string {
  const params = new URLSearchParams({
    href: pageUrl,
    tabs: "timeline",
    width: String(width),
    height: "620",
    small_header: "true",
    adapt_container_width: "true",
    hide_cover: "false",
    show_facepile: "false",
  })
  return `https://www.facebook.com/plugins/page.php?${params.toString()}`
}
